import { useState } from "react";
import api from "../lib/api";
import { CheckCircle2, XCircle, Loader2, AlertTriangle } from "lucide-react";

const _norm = (s) => (s || "").toLowerCase().replace(/[^a-z]/g, "");

// GSTIN input with a Verify button. On success calls onVerified({state, pan, legal_name, trade_name, address}).
// Verification is offline (format + checksum + state/PAN). If an `address` prop is passed, after a valid
// verify we resolve the address's state (Google geocode via /places/resolve) and WARN when it differs from
// the GSTIN's state — a common data-entry mistake (billing address doesn't match the GST registration state).
export function GstVerifyField({ value = "", onChange, onVerified, address = "", testId = "gst", className = "input-field font-mono", placeholder = "15-digit GSTIN" }) {
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null); // { ok, msg }
  const [warn, setWarn] = useState("");

  async function verify() {
    const g = (value || "").trim().toUpperCase();
    if (!g) { setStatus({ ok: false, msg: "Enter a GSTIN first" }); setWarn(""); return; }
    setBusy(true);
    setWarn("");
    try {
      const { data } = await api.get("/gst/verify", { params: { gstin: g } });
      if (data.valid) {
        setStatus({ ok: true, msg: `Valid · ${data.state} · PAN ${data.pan}` });
        onVerified && onVerified(data);
        if (address && address.trim()) {
          try {
            const { data: pl } = await api.get("/places/resolve", { params: { address } });
            const addrState = pl?.state || "";
            if (addrState && _norm(addrState) !== _norm(data.state)) {
              setWarn(`Billing address is in ${addrState}, but GSTIN is registered in ${data.state}. Double-check the GST/address.`);
            }
          } catch { /* address resolve is best-effort */ }
        }
      } else {
        setStatus({ ok: false, msg: data.error || "Invalid GSTIN" });
      }
    } catch {
      setStatus({ ok: false, msg: "Could not verify GSTIN" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="flex gap-2">
        <input
          className={`${className} flex-1`}
          value={value}
          maxLength={15}
          placeholder={placeholder}
          onChange={(e) => { onChange(e.target.value.toUpperCase()); setStatus(null); setWarn(""); }}
          data-testid={`${testId}-input`}
        />
        <button type="button" className="btn-secondary shrink-0" onClick={verify} disabled={busy} data-testid={`${testId}-verify`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify"}
        </button>
      </div>
      {status && (
        <div className={`text-[11px] mt-1 flex items-center gap-1 ${status.ok ? "text-[#2F6F4E]" : "text-[#8A2526]"}`} data-testid={`${testId}-status`}>
          {status.ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />} {status.msg}
        </div>
      )}
      {warn && (
        <div className="text-[11px] mt-1 flex items-start gap-1 text-[#A0522D]" data-testid={`${testId}-state-warn`}>
          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> {warn}
        </div>
      )}
    </div>
  );
}
